const INT_MIN = -2E9;
const INT_MAX = 2E9;

// Convert dimension in arbitrary units to px
// ch units are fudged for the android and desktop platforms due to an unusually narrow zero
// in the Roboto and Verdana/DejaVu Sans fonts
const topx = dim => (dim && ({
        ch: $build.platform == 'desktop' ? 11 : 10,
        em: 16,
        ex: 8,
        pc: 16,
        px: 1,
        pt: 1.333,
        rem: 16
    }[dim.replace(/^[\d.]+/, '')] * parseFloat(dim))) || 0;

// Convert ch dimension to px for consistency
const adjust = dim => /ch$/.test(dim) ? parseFloat(dim) * ($build.platform == 'desktop' ? 11 : 10) + 'px' : dim;

// Detect support for `position: sticky;`
const haveSticky = (function() {
    const el = document.createElement('i');
    const style = el.style;
    style.position = '-webkit-sticky';
    style.position = 'sticky';
    return style.position.endsWith('sticky');
})();

// Drop priority sort predicate
const predicate = (a, b) => {
    if (!a.drop) {
        return -1;
    } else if (!b.drop) {
        return 1;
    } else if (a.drop === 'drop' || a.drop === true) {
        return b.drop === 'drop' || b.drop === true ? topx(b.width) - topx(a.width) : -1;
    } else if (b.drop === 'drop' || b.drop === true) {
        return -1;
    } else {
        return a.drop === b.drop ? topx(b.width) - topx(a.width) : b.drop - a.drop;
    }
};


/**
 * <colgroup> template internal to [ib-table](#ibtable).
 * This compartmentalizes rendering for performance.
 */
export default {
    name: 'ib-table-colgroup',

    props: {
        col: Array,
        ul: Boolean
    },

    data() {
        return {
            drop: [],
            dropMin: 0,     // next lower breakpoint
            dropMax: 0,     // next higher breakpoint
            wide: false,    // true if table is wider than sum of columns
            style: null     // <style> element generated on the fly
        }
    },

    computed: {

        /**
         * The sum in pixels of all forced column widths.
         */
        colSumForce() {
            const col = this.col;
            const drop = this.drop;
            const wide = this.wide;
            let sum = 0;
            for (let i = 0; i < col.length; i++) {
                sum += !drop.includes(col[i]) ? (col[i].force || !wide && col[i].sticky) ? topx(col[i].width) : 0 : 0;
            }
            return sum;
        },

        /**
         * The sum in pixels of all non-forced column widths.
         */
        colSumRel() {
            const col = this.col;
            const drop = this.drop;
            const wide = this.wide;
            let sum = 0;
            for (let i = 0; i < col.length; i++) {
                sum += !drop.includes(col[i]) ? (col[i].force || !wide && col[i].sticky) ? 0 : topx(col[i].width) : 0;
            }
            return sum;
        },

        /**
         * Array of rendered column widths. If there are any forced width columns, this
         * will replace non-forced widths with percentages, to give them higher stretch
         * priority than the forced columns. The `calc(x% - 1px)` usage for percentages
         * works around a rendering bug in Chrome.
         */
        colWidth() {
            const drop = this.drop;
            const wide = this.wide;
            return this.col.map(
                this.colSumForce
                    ? col => drop.includes(col) ? 0 : !topx(col.width) ? col.width : (col.force || !wide && col.sticky) ? adjust(col.width) : (topx(col.width) / this.colSumRel) * 100 + '%'
                    : col => drop.includes(col) ? 0 : adjust(col.width));
        },

        colBreak() {
            return this.col.slice().sort(predicate);
        }
    },

    watch: {
        col() {
            this.refresh();
        }
    },

    render(h) {
        return h('colgroup', {
            'ref': 'colgroup'
        },
        this.col.map((col,i) => h('col', {
            'class': this.drop.includes(col) ? 'hide' : null,
            'style': {
                'width': this.colWidth[i]
            }
        })));
    },

    beforeDestroy() {
        if (this.style) {
            this.style.remove();
        }
    },

    methods: {
        refresh() {
            this.dropUpdate(this.$parent.$el.clientWidth);
            this.styleUpdate();
        },

        /**
         * The sum in pixels of all non-droppable column widths.
         */
        minWidth() {
            const col = this.col;
            const drop = this.drop;
            let sum = 0;
            for (let i = 0; i < col.length; i++) {
                sum += col[i].drop ? 0 : topx(col[i].width);
            }
            return sum;
        },

        /**
         * The sum in pixels of all column widths.
         */
        maxWidth() {
            const col = this.col;
            let sum = 0;
            for (let i = 0; i < col.length; i++) {
                sum += topx(col[i].width);
            }
            return sum;
        },

        /**
         * Manage CSS column widths on resize event.
         */
        dropUpdate(width) {
            this.wide = width > this.maxWidth();
            if (width < this.dropMin || width >= this.dropMax) {
                const brk = this.colBreak;
                let i = 0;
                let j = 0;
                let last = 0;
                let total = 0;
                while (i < brk.length) {
                    last = total;
                    if (brk[i].drop) {
                        while (j <= i) {
                            total += topx(brk[j++].width);
                        }
                        if (total > width) {
                            break;
                        }
                    }
                    i++;
                }
                this.drop = brk.slice(i);
                this.dropMin = i < brk.length ? last : total;
                this.dropMax = i < brk.length ? total : INT_MAX;
            }
        },

        /**
         * Generate a stylesheet on the fly setting column properties such as alignment or stickiness.
         */
        styleUpdate() {
            const id = '#' + this.$parent._id;
            const col = this.col;
            const width = this.colWidth;
            const rtl = this.$rtl;

            let ss = 0;
            let es = 0;
            let si = 0;
            let off;

            let style = this.style;
            if (style) {
                while (style.sheet.cssRules.length) {
                    style.sheet.deleteRule(style.sheet.cssRules.length - 1);
                }
            } else {
                style = this.style = document.createElement('style');
                style.appendChild(document.createTextNode(''));
                document.head.appendChild(style);
            }

            // Tables with underlays should not horizontally scroll
            const minWidth = this.ul ? 0 : this.minWidth();
            if (minWidth) {
                style.sheet.insertRule(id + '{min-width:' + minWidth + 'px}', si++);
            }

            for (let i = 0; i < col.length; i++) {
                let td = '';
                let th = '';
                let c = col[i];
                if (c.numeric === '' || c.numeric === 'numeric' || c.numeric === true) {
                    td +=
                        'text-align:' + 'right;' +
                        'font-feature-settings:' + '"tnum" 1;' +
                        'font-variant-numeric:' + 'tabular-nums;';
                    th +=
                        'padding-left:' + '0;';
                }
                if (c.align === 'start') {
                    td +=
                        'text-align:' + 'left;' +
                        'text-align:' + 'start;';
                    th +=
                        '-webkit-padding-end:' + '0;' +
                        'padding-inline-end:' + '0;';
                }
                else if (c.align === 'center') {
                    td +=
                        'text-align:' + 'center;';
                }
                else if (c.align === 'end') {
                    td +=
                        'text-align:' + 'right;' +
                        'text-align:' + 'end;';
                    th +=
                        '-webkit-padding-start:' + '0;' +
                        'padding-inline-start:' + '0;';
                }
                if (c.sticky === 'start' || c.sticky === '' || c.sticky === 'sticky' || c.sticky === true) {
                    let off = 0;
                    for (let j = 0; j < i; j++) {
                        off += topx(col[j].width);
                    }
                    td +=
                        'position:' + '-webkit-sticky;' +
                        'position:' + 'sticky;' +
                        (rtl ? 'right:' : 'left:') + off + 'px;' +
                        'z-index:' + '2;';
                    th +=
                        'z-index:' + '3;';
                    ss = i;
                }
                else if (c.sticky === 'end') {
                    let off = 0;
                    for (let j = i + 1; j < col.length; j++) {
                        off += topx(col[j].width);
                    }
                    td +=
                        'position:' + '-webkit-sticky;' +
                        'position:' + 'sticky;' +
                        (rtl ? 'left:' : 'right:') + off + 'px;' +
                        'z-index:' + '2;';
                    th +=
                        'z-index:' + '3;';
                    if (es < 0) {
                        es = i;
                    }
                }
                if (td) {
                    style.sheet.insertRule(id + ' tr>:nth-child(' + (i+1) + '){' + td + '}', si++);
                }
                if (th) {
                    style.sheet.insertRule(id + ' th:nth-child(' + (i+1) + '){' + th + '}', si++);
                }
                if (c.sort) {
                    style.sheet.insertRule(id + '._sh' + (i+1) + ' tr:not(._act)>:nth-child(' + (i+1) + '){background-color:var(--highlight1)}', si++);
                    style.sheet.insertRule('._tbrs ' + id + '._sh' + (i+1) + ' tbody tr:not(._act):nth-child(2n)>:nth-child(' + (i+1) + '){background-color:var(--highlight2)}', si++);
                }
            }

            off = 0;
            for (let i = 0; i <= ss; i++) {
                off += topx(col[i].width);
            }
            style.sheet.insertRule(id + ' ._tbs' + (rtl ? 'r{right:' : 'l{left:') + off + 'px;}', si++);

            off = 0;
            if (es) {
                for (let i = es; i < col.length; i++) {
                    off += topx(col[i].width);
                }
            }
            style.sheet.insertRule(id + ' ._tbs' + (rtl ? 'l{left:' : 'r{right:') + off + 'px;}', si++);

            if (haveSticky) {
                this.$parent.stickyStart = ss;
                this.$parent.stickyEnd = es;
            }
        }
    }
}
